import React, { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  AlertTriangle,
  ChevronDown,
  ChevronRight,
  Loader2,
  Sparkles,
  Wand2,
} from "lucide-react";
import { toast } from "sonner";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Slider } from "@/components/ui/slider";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { Skeleton } from "@/components/ui/skeleton";

import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth.jsx";
import { useSystemConfig } from "@/lib/systemConfig";
import AIGenerationProgressCard from "@/components/AIGenerationProgressCard";

// Slider bounds — wider on the backend (1-200 / 0-$50 / 10-3600s) but
// the UI sliders cover the realistic working range.  The form still
// sends the exact value, just bounded.
const STEPS_MIN = 10;
const STEPS_MAX = 100;
const STEPS_DEFAULT = 50;
const COST_MIN_USD = 0.5;
const COST_MAX_USD = 20;
const COST_DEFAULT_USD = 5;
const DURATION_MIN_S = 60;
const DURATION_MAX_S = 1800;
const DURATION_DEFAULT_S = 600;

const BRIEF_PLACEHOLDER =
  "Log in to Eyvo, navigate to the Requisitions area, and start creating a new requisition with a freeform line item description 'Office paper, 5 reams'.";
const BRIEF_MAX_LEN = 1000;

// localStorage key for resume-on-reload.  Scoped per workflow so
// jumping between workflows doesn't accidentally resume the wrong run.
function STORAGE_KEY(workflowId) {
  return `eda.ai-gen.${workflowId}`;
}

function readStored(workflowId) {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY(workflowId));
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function writeStored(workflowId, value) {
  if (typeof window === "undefined") return;
  try {
    if (value == null) {
      window.localStorage.removeItem(STORAGE_KEY(workflowId));
    } else {
      window.localStorage.setItem(STORAGE_KEY(workflowId), JSON.stringify(value));
    }
  } catch {
    // localStorage can throw in private mode / when quota is exceeded
    // — neither is fatal to the user flow.
  }
}

function fmtCost(n) {
  const v = Number(n) || 0;
  return `$${v.toFixed(v >= 1 ? 2 : 4)}`;
}

function fmtDuration(s) {
  const total = Math.max(0, Math.round(Number(s) || 0));
  if (total < 60) return `${total}s`;
  const m = Math.floor(total / 60);
  const sec = total % 60;
  return sec === 0 ? `${m}m` : `${m}m ${sec}s`;
}

/**
 * R3 Phase 6A.5 — top-level panel mounted on the Workflow Detail page.
 *
 *   • Hidden for viewer role (gate uses `workflow.write` permission)
 *   • Shows resume UI when a generation is in flight against this workflow
 *   • Otherwise renders the form → modal → kick-off flow
 */
export default function AIStepGenerationPanel({ workflow, testid = "ai-step-generation-panel" }) {
  const { config: sysConfig, ready: sysReady } = useSystemConfig();
  const { hasPermission } = useAuth();
  const canWrite = hasPermission("workflow.write");

  // ---- in-flight probe — decides whether the panel renders the form
  //      or remounts the live progress card on reload.
  const inFlight = useQuery({
    queryKey: ["ai-in-flight"],
    queryFn: async () => (await api.get("/workflows/ai-generate-steps/in-flight")).data,
    refetchInterval: false,
    staleTime: 30_000,
    enabled: canWrite,
  });

  // ---- form state
  const [aiBrief, setAiBrief] = useState("");
  const [targetUrl, setTargetUrl] = useState("");
  const [maxSteps, setMaxSteps] = useState(STEPS_DEFAULT);
  const [maxCostUsd, setMaxCostUsd] = useState(COST_DEFAULT_USD);
  const [maxDurationSeconds, setMaxDurationSeconds] = useState(DURATION_DEFAULT_S);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  // R3 Phase 6A.6 — optional chain auto-trigger.  Lives in the confirm
  // modal but the state is hoisted so it can be passed into the kickoff
  // payload and echoed back into the active-generation object so the
  // progress card knows whether to call /autorun on terminal_summary.
  const [autoRunOnSuccess, setAutoRunOnSuccess] = useState(false);

  // ---- modal + live progress state
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [estimate, setEstimate] = useState(null);
  const [estimateLoading, setEstimateLoading] = useState(false);
  const [estimateError, setEstimateError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [activeGeneration, setActiveGeneration] = useState(null);
  // ^ shape: { generation_id, stream_url, cancel_url, caps:{max_steps,...} }

  // Fill the target URL default from system config once it lands.
  useEffect(() => {
    if (!targetUrl && sysReady && sysConfig.eyvo_demo_url) {
      setTargetUrl(sysConfig.eyvo_demo_url);
    }
  }, [sysReady, sysConfig.eyvo_demo_url, targetUrl]);

  // Hydrate active generation from localStorage on first mount IF the
  // backend confirms the lock is still held by the same generation_id.
  useEffect(() => {
    if (!inFlight.data) return;
    const flight = inFlight.data.in_flight;
    if (!flight) {
      // Either nothing running or the run finished between page mount
      // and our probe — wipe stale localStorage just in case.
      const stored = readStored(workflow.workflow_id);
      if (stored) writeStored(workflow.workflow_id, null);
      return;
    }
    if (flight.workflow_id !== workflow.workflow_id) {
      // Some other workflow is generating right now — the form is
      // disabled below, but no resume UI on this page.
      return;
    }
    const stored = readStored(workflow.workflow_id);
    if (stored && stored.generation_id === flight.generation_id) {
      setActiveGeneration({
        generation_id: stored.generation_id,
        stream_url: stored.stream_url,
        cancel_url: stored.cancel_url,
        caps: stored.caps,
        auto_run_on_success: !!stored.auto_run_on_success,
      });
    } else {
      // Backend says something IS running on this workflow but we don't
      // have its URLs locally (e.g. user opened a fresh tab).  Build
      // the URLs from the convention the backend exposes — the SSE
      // endpoint accepts the same generation_id format regardless of
      // who connects.
      const generation_id = flight.generation_id;
      setActiveGeneration({
        generation_id,
        stream_url: `/api/workflows/${workflow.workflow_id}/ai-generate-steps/${generation_id}/stream`,
        cancel_url: `/api/workflows/${workflow.workflow_id}/ai-generate-steps/${generation_id}/cancel`,
        // Best-effort caps — without localStorage we don't know the
        // user's chosen caps, so fall back to defaults.  The meters
        // will render with the defaults until the live frames overwrite
        // them; not perfect but better than the alternative (no UI).
        caps: stored?.caps || {
          max_steps: STEPS_DEFAULT,
          max_cost_usd: COST_DEFAULT_USD,
          max_duration_seconds: DURATION_DEFAULT_S,
        },
        // The terminal_summary frame will carry the authoritative
        // auto_run_on_success flag anyway — if we picked it up across
        // tab boundaries we'd defer to that.  For local resume we
        // honor the stored value.
        auto_run_on_success: !!stored?.auto_run_on_success,
      });
    }
  }, [inFlight.data, workflow.workflow_id]);

  const briefValid = aiBrief.trim().length > 0;
  const urlValid = /^https?:\/\//i.test(targetUrl.trim());
  const formValid = briefValid && urlValid && canWrite;

  // Compose the body the backend expects — used twice (estimate + go).
  const payload = useMemo(
    () => ({
      ai_brief: aiBrief.trim(),
      target_url: targetUrl.trim(),
      max_steps: maxSteps,
      max_cost_usd: maxCostUsd,
      max_duration_seconds: maxDurationSeconds,
      auto_run_on_success: autoRunOnSuccess,
    }),
    [aiBrief, targetUrl, maxSteps, maxCostUsd, maxDurationSeconds, autoRunOnSuccess],
  );

  async function openConfirm() {
    if (!formValid) return;
    setEstimateError(null);
    setEstimate(null);
    setConfirmOpen(true);
    setEstimateLoading(true);
    try {
      const { data } = await api.post(
        `/workflows/${workflow.workflow_id}/ai-generate-steps/estimate`,
        payload,
      );
      setEstimate(data);
    } catch (err) {
      setEstimateError(formatApiError(err));
    } finally {
      setEstimateLoading(false);
    }
  }

  async function kickOff() {
    if (!formValid || submitting) return;
    setSubmitting(true);
    try {
      const { data } = await api.post(
        `/workflows/${workflow.workflow_id}/ai-generate-steps?mode=async`,
        payload,
      );
      const caps = {
        max_steps: payload.max_steps,
        max_cost_usd: payload.max_cost_usd,
        max_duration_seconds: payload.max_duration_seconds,
      };
      const active = {
        generation_id: data.generation_id,
        stream_url: data.stream_url,
        cancel_url: data.cancel_url,
        caps,
        auto_run_on_success: payload.auto_run_on_success,
      };
      writeStored(workflow.workflow_id, {
        ...active,
        ai_brief: payload.ai_brief,
        target_url: payload.target_url,
        started_at: new Date().toISOString(),
      });
      setActiveGeneration(active);
      setConfirmOpen(false);
      toast.success("Generation started — watch the live progress below.");
    } catch (err) {
      const status = err?.response?.status;
      if (status === 409) {
        toast.error(
          "Another AI generation is already running. Wait for it to finish or cancel it first.",
        );
      } else {
        toast.error(formatApiError(err));
      }
    } finally {
      setSubmitting(false);
    }
  }

  function handleTerminalReached() {
    // Generation finished — drop localStorage so a reload doesn't
    // try to re-subscribe to a stream that no longer has a queue.
    // BUT keep `activeGeneration` in state so the TerminalCard stays
    // mounted until the user explicitly dismisses (or clicks View /
    // Auto-run).  Background queries get invalidated inside the
    // progress card.
    writeStored(workflow.workflow_id, null);
  }

  function handleDismissActive() {
    setActiveGeneration(null);
    writeStored(workflow.workflow_id, null);
  }

  // Hide the panel entirely for viewer role — the brief calls for
  // builder+ only.  Also hide when the parent workflow is archived
  // (no point generating steps for something you can't run).
  if (!canWrite) return null;
  if (workflow.status !== "active") return null;

  // Some other workflow is in flight — banner explaining the gate.
  const otherInFlight =
    inFlight.data?.in_flight &&
    inFlight.data.in_flight.workflow_id &&
    inFlight.data.in_flight.workflow_id !== workflow.workflow_id;

  // Live progress takes over the panel when active.
  if (activeGeneration) {
    return (
      <section className="space-y-2" data-testid={`${testid}-active`}>
        <SectionHeader />
        <AIGenerationProgressCard
          workflowId={workflow.workflow_id}
          generationId={activeGeneration.generation_id}
          streamUrl={activeGeneration.stream_url}
          cancelUrl={activeGeneration.cancel_url}
          caps={activeGeneration.caps}
          autoRunOnSuccess={!!activeGeneration.auto_run_on_success}
          onTerminalReached={handleTerminalReached}
          onDismiss={handleDismissActive}
          testid={`${testid}-progress-card`}
        />
      </section>
    );
  }

  return (
    <section
      className="space-y-4 rounded-xl border border-slate-200 bg-white p-5 shadow-sm"
      data-testid={testid}
    >
      <SectionHeader />

      {otherInFlight && (
        <div
          className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-[12px] text-amber-800"
          data-testid={`${testid}-other-in-flight-banner`}
        >
          <AlertTriangle size={14} className="mt-0.5 flex-none" />
          <div>
            Another AI generation is running on workflow{" "}
            <code className="font-mono">{inFlight.data.in_flight.workflow_id.slice(0, 8)}</code>
            . Only one tenant-wide generation runs at a time — the form is
            disabled until it finishes.
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 gap-4 md:grid-cols-12">
        <div className="md:col-span-8">
          <Label htmlFor="ai-brief">
            What should the agent do?
            <span className="ml-1 text-[11px] font-normal text-slate-400">
              Plain English, no selectors.
            </span>
          </Label>
          <Textarea
            id="ai-brief"
            rows={4}
            value={aiBrief}
            maxLength={BRIEF_MAX_LEN}
            placeholder={BRIEF_PLACEHOLDER}
            onChange={(e) => setAiBrief(e.target.value.slice(0, BRIEF_MAX_LEN))}
            disabled={!!otherInFlight}
            data-testid={`${testid}-ai-brief`}
            className="mt-1 text-sm"
          />
          <div className="mt-1 flex items-center justify-between text-[11px] text-slate-400">
            <span>Plain English; the agent will translate into selectors.</span>
            <span
              data-testid={`${testid}-ai-brief-counter`}
              className={aiBrief.length >= BRIEF_MAX_LEN ? "text-rose-500" : ""}
            >
              {aiBrief.length} / {BRIEF_MAX_LEN}
            </span>
          </div>
        </div>
        <div className="md:col-span-4">
          <Label htmlFor="ai-target-url">Target URL</Label>
          <Input
            id="ai-target-url"
            value={targetUrl}
            onChange={(e) => setTargetUrl(e.target.value)}
            placeholder="https://ibuy.cloud"
            disabled={!!otherInFlight}
            data-testid={`${testid}-target-url`}
            className="mt-1 font-mono text-sm"
          />
          <p className="mt-1 text-[11px] text-slate-400">
            The agent starts here. Login flow is pre-baked.
          </p>
        </div>
      </div>

      <Collapsible open={advancedOpen} onOpenChange={setAdvancedOpen}>
        <CollapsibleTrigger asChild>
          <button
            type="button"
            className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-600 hover:text-slate-900"
            data-testid={`${testid}-advanced-toggle`}
          >
            {advancedOpen ? (
              <ChevronDown size={14} />
            ) : (
              <ChevronRight size={14} />
            )}
            Advanced settings
          </button>
        </CollapsibleTrigger>
        <CollapsibleContent
          className="mt-3 grid grid-cols-1 gap-5 rounded-lg border border-slate-200 bg-slate-50/60 p-4 md:grid-cols-3"
          data-testid={`${testid}-advanced`}
        >
          <SliderRow
            label="Max steps"
            value={maxSteps}
            valueDisplay={`${maxSteps}`}
            min={STEPS_MIN}
            max={STEPS_MAX}
            step={1}
            onChange={(v) => setMaxSteps(v)}
            testid={`${testid}-max-steps`}
            hint="Iterations including scrolls. 50 = enough for a deep flow."
          />
          <SliderRow
            label="Max cost (USD)"
            value={maxCostUsd}
            valueDisplay={fmtCost(maxCostUsd)}
            min={COST_MIN_USD}
            max={COST_MAX_USD}
            step={0.5}
            onChange={(v) => setMaxCostUsd(v)}
            testid={`${testid}-max-cost`}
            hint="Hard cap — generation stops the moment this is crossed."
          />
          <SliderRow
            label="Max duration"
            value={maxDurationSeconds}
            valueDisplay={fmtDuration(maxDurationSeconds)}
            min={DURATION_MIN_S}
            max={DURATION_MAX_S}
            step={30}
            onChange={(v) => setMaxDurationSeconds(v)}
            testid={`${testid}-max-duration`}
            hint="Wall-clock. Slow networks may need a higher cap."
          />
        </CollapsibleContent>
      </Collapsible>

      <div className="flex items-center justify-between">
        <p className="text-[11px] text-slate-400">
          The agent reads the live DOM and generates steps incrementally.
          You'll review costs before confirming.
        </p>
        <Button
          type="button"
          onClick={openConfirm}
          disabled={!formValid || !!otherInFlight}
          className="gap-1.5 bg-rose-600 hover:bg-rose-700 disabled:bg-slate-300"
          data-testid={`${testid}-generate-button`}
        >
          <Wand2 size={15} />
          Generate Steps
        </Button>
      </div>

      <Dialog open={confirmOpen} onOpenChange={(o) => !submitting && setConfirmOpen(o)}>
        <DialogContent
          className="max-w-lg"
          data-testid={`${testid}-confirm-dialog`}
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles size={16} className="text-rose-600" />
              Generate steps with AI
            </DialogTitle>
            <DialogDescription>
              Review the estimate before kicking off. The generation will stop
              the moment any cap is reached.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            <SummaryRow
              label="Brief"
              value={
                <span className="line-clamp-2 text-sm font-medium text-slate-800">
                  {aiBrief || "—"}
                </span>
              }
              testid={`${testid}-confirm-brief`}
            />
            <SummaryRow
              label="Target URL"
              value={
                <code className="break-all font-mono text-xs text-slate-700">
                  {targetUrl}
                </code>
              }
              testid={`${testid}-confirm-target`}
            />
            <SummaryRow
              label="Caps"
              value={
                <span className="font-mono text-xs text-slate-700">
                  {maxSteps} steps · {fmtCost(maxCostUsd)} · {fmtDuration(maxDurationSeconds)}
                </span>
              }
              testid={`${testid}-confirm-caps`}
            />
            <div
              className="rounded-lg border border-slate-200 bg-slate-50 p-3"
              data-testid={`${testid}-confirm-estimate`}
            >
              <div className="mb-2 text-[10px] font-semibold uppercase tracking-wider text-slate-500">
                Estimate
              </div>
              {estimateLoading ? (
                <div className="flex items-center gap-2 text-sm text-slate-500">
                  <Loader2 size={14} className="animate-spin" />
                  Crunching numbers…
                </div>
              ) : estimateError ? (
                <p className="text-sm text-rose-600">{estimateError}</p>
              ) : estimate ? (
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <div className="text-[11px] uppercase tracking-wider text-slate-400">
                      Cost range
                    </div>
                    <div
                      className="font-mono text-slate-900"
                      data-testid={`${testid}-confirm-cost-range`}
                    >
                      {fmtCost(estimate.estimated_cost_range_usd?.low)}
                      <span className="px-1 text-slate-400">–</span>
                      {fmtCost(estimate.estimated_cost_range_usd?.high)}
                    </div>
                  </div>
                  <div>
                    <div className="text-[11px] uppercase tracking-wider text-slate-400">
                      Duration range
                    </div>
                    <div
                      className="font-mono text-slate-900"
                      data-testid={`${testid}-confirm-duration-range`}
                    >
                      {fmtDuration(estimate.estimated_duration_range_seconds?.low)}
                      <span className="px-1 text-slate-400">–</span>
                      {fmtDuration(estimate.estimated_duration_range_seconds?.high)}
                    </div>
                  </div>
                </div>
              ) : (
                <Skeleton className="h-8 w-full" />
              )}
            </div>
            <div
              className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-[12px] text-amber-800"
              data-testid={`${testid}-confirm-warning`}
            >
              <AlertTriangle size={14} className="mt-0.5 flex-none" />
              <span>
                AI-generated steps may need manual review. First-generation
                success rate is typically 75-85%.
              </span>
            </div>
            {/* R3 Phase 6A.6 — optional auto-run on success.  When the
                generation finishes with success_count/steps_count >= 0.5
                we'll automatically kick off a real workflow run and the
                narration/voiceover/stitch chain.  Off by default — the
                user opts in here. */}
            <label
              className="flex cursor-pointer items-start gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-[12px] text-slate-700 hover:border-slate-300"
              data-testid={`${testid}-confirm-autorun-toggle`}
            >
              <input
                type="checkbox"
                className="mt-0.5 h-3.5 w-3.5 cursor-pointer accent-rose-600"
                checked={autoRunOnSuccess}
                onChange={(e) => setAutoRunOnSuccess(e.target.checked)}
                data-testid={`${testid}-confirm-autorun-checkbox`}
              />
              <div className="-mt-0.5">
                <div className="font-medium text-slate-800">
                  Auto-run on successful generation
                </div>
                <p className="text-slate-500">
                  When generation completes, automatically execute the
                  workflow and produce the final video (narration +
                  voiceover + stitched MP4). Skipped if the success
                  rate is below 50%.
                </p>
              </div>
            </label>
          </div>

          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setConfirmOpen(false)}
              disabled={submitting}
              data-testid={`${testid}-confirm-cancel`}
              className="border-slate-300 text-slate-700"
            >
              Cancel
            </Button>
            <Button
              onClick={kickOff}
              disabled={submitting || !formValid || estimateLoading}
              className="gap-1.5 bg-rose-600 hover:bg-rose-700 disabled:bg-slate-300"
              data-testid={`${testid}-confirm-go`}
            >
              {submitting ? (
                <>
                  <Loader2 size={14} className="animate-spin" />
                  Starting…
                </>
              ) : (
                <>
                  <Wand2 size={14} />
                  Generate ({fmtCost(maxCostUsd)} cap)
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  );
}

function SectionHeader() {
  return (
    <div className="flex items-center gap-2">
      <Sparkles size={16} className="text-rose-600" />
      <h2 className="text-sm font-semibold tracking-tight text-slate-900">
        AI step generation
      </h2>
      <span className="rounded-full bg-rose-50 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider text-rose-700 ring-1 ring-rose-200">
        Experimental
      </span>
    </div>
  );
}

function SliderRow({ label, value, valueDisplay, min, max, step, onChange, testid, hint }) {
  return (
    <div className="space-y-1">
      <div className="flex items-baseline justify-between">
        <Label className="text-xs text-slate-700">{label}</Label>
        <span
          className="font-mono text-xs font-semibold text-slate-900"
          data-testid={`${testid}-readout`}
        >
          {valueDisplay}
        </span>
      </div>
      <Slider
        min={min}
        max={max}
        step={step}
        value={[value]}
        onValueChange={(v) => onChange(v[0])}
        data-testid={testid}
        className="mt-1"
      />
      {hint && <p className="text-[10px] text-slate-400">{hint}</p>}
    </div>
  );
}

function SummaryRow({ label, value, testid }) {
  return (
    <div className="grid grid-cols-12 gap-3" data-testid={testid}>
      <div className="col-span-3 text-[11px] uppercase tracking-wider text-slate-400">
        {label}
      </div>
      <div className="col-span-9">{value}</div>
    </div>
  );
}
